import { use } from "chai";
import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Link } from "components/DesignSystem/Link/Link";
import { DividerLine } from "components/icons/DividerLine";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { EmptyList } from "components/Illustrations/EmptyList";
import { JournalEntryLogSlider } from "components/JournalEntry/JournalEntryLogSlider";
import QuillEditor from "components/QuillEditor/QuillEditor";
import { AUDIT_LOG_ACTIONS } from "constants/auditlogActions";
import { AUDIT_LOG_KEYS, AuditLogKey } from "constants/auditlogKeys";
import { AUDIT_LOG_OWNER_TYPE } from "constants/auditLogOwnerType";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { usePaginatedQuery } from "hooks/usePaginatedQuery";
import { useShow } from "hooks/useShow";
import Quill from "quill";
import { useEffect, useMemo, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  AuditLog,
  useGetByObjectIdAuditLogsQuery,
  useGetInfiniteByObjectIdAuditLogsInfiniteQuery,
  useLazyGetByObjectIdAuditLogsQuery,
} from "store/apis/auditlogs";
import { formatDate, formatTime } from "utils/formatDate";
import { stopPropagation } from "utils/stopPropagation";

const FormatLog = ({
  changeValue,
  changeKey,
  dataType = "text",
}: {
  changeValue?: string;
  changeKey: string;
  dataType?: "text" | "date";
}) => {
  const quillRef = useRef<Quill | undefined | null>(null);
  if (!changeValue) {
    return "None";
  }

  if (changeValue === "None") {
    return changeValue;
  }

  if (changeKey === AUDIT_LOG_KEYS.TAG) {
    return `#${changeValue || "None"}`;
  }

  if (changeKey === AUDIT_LOG_KEYS.NOTE) {
    return (
      <div className="recentNote">
        <QuillEditor
          initReadOnly
          theme="bubble"
          defaultValue={JSON.parse(changeValue || "[]")}
          parentRef={quillRef}
          onTextChange={() => {}}
        />
      </div>
    );
  }

  if (changeKey === AUDIT_LOG_KEYS.AMOUNT) {
    return <AmountSuperScript amount={Number(changeValue || 0)} />;
  }

  if (changeKey === AUDIT_LOG_KEYS.DATE || dataType === "date") {
    return <span className="t-text-text-30">{formatDate(changeValue)}</span>;
  }

  return changeValue || "None";
};

const Resource = ({ log }: { log: AuditLog }) => {
  const { link } = useConstructInternalLink();

  const changeKey = Object.keys(log.audited_changes || {})[0] as AuditLogKey;
  const afterValue = log.audited_changes?.[changeKey]?.after?.uuid;

  if (
    changeKey === AUDIT_LOG_KEYS.INVOICE &&
    log.action === AUDIT_LOG_ACTIONS.LINKED
  ) {
    return (
      <>
        <Link
          to={link(`/books/invoices-and-customers/${afterValue}`)}
          target="_blank"
        >
          <span onClick={stopPropagation}>{log.resource}</span>
        </Link>{" "}
        {log.action}
      </>
    );
  }

  return `${log.resource} ${log.action}`;
};

export const TransitionLog = ({ log }: { log: AuditLog }) => {
  const [changeKey, auditedChanges] = Object.entries(
    log.audited_changes || {}
  )[0] || [null, null];

  const hasBeforeValue = Boolean(auditedChanges?.before?.value);
  const hasAfterValue = Boolean(auditedChanges?.after?.value);

  const transactionStatusUpdate =
    changeKey === AUDIT_LOG_KEYS.STATUS &&
    log.obj?.content_type === "entitytransaction";

  const isNewChange = [
    AUDIT_LOG_KEYS.COMMENT,
    AUDIT_LOG_KEYS.INVOICE,
    AUDIT_LOG_KEYS.TAG,
    AUDIT_LOG_KEYS.NOTE,
  ].some((key) => key === changeKey);

  const noChange =
    (!hasBeforeValue && !hasAfterValue) ||
    transactionStatusUpdate ||
    (log.action === AUDIT_LOG_ACTIONS.CREATED &&
      log.obj?.content_type === "ledgerentry");

  if (log.event.name) {
    return log.event.name;
  }

  if (noChange) {
    return <Resource log={log} />;
  }

  if (isNewChange) {
    const value =
      auditedChanges?.after?.value || auditedChanges?.before?.value || "None";

    return (
      <>
        <Resource log={log} />:{" "}
        <span className="t-text-text-30 t-text-body-sm">
          <FormatLog
            changeValue={value}
            changeKey={changeKey}
            dataType={auditedChanges?.data_type}
          />
        </span>
      </>
    );
  }

  return (
    <>
      <Resource log={log} />:{" "}
      <span className="t-text-text-30 t-text-body-sm">
        <span>
          <FormatLog
            changeValue={auditedChanges?.before?.value}
            changeKey={changeKey}
            dataType={auditedChanges?.data_type}
          />
        </span>
        <span> → </span>
        <span>
          <FormatLog
            changeValue={auditedChanges?.after?.value}
            changeKey={changeKey}
            dataType={auditedChanges?.data_type}
          />
        </span>
      </span>
    </>
  );
};

const Owner = ({ owner }: { owner: AuditLog["owner"] }) => {
  const { link } = useConstructInternalLink();

  if (owner.user_type === AUDIT_LOG_OWNER_TYPE.RULE) {
    return (
      <Link
        to={link(`/books/transactions/rules`, {
          moreQuery: {
            rule_id: owner.id,
          },
        })}
        className="t-items-center t-gap-1 t-inline-flex"
      >
        <span>{owner.user_type}</span>
        <span className="t-text-neutral-20">
          <DividerLine />
        </span>
      </Link>
    );
  }

  return (
    <span className="t-items-center t-gap-1 t-inline-flex">
      <span>{owner.name || owner.user_type}</span>
      <span className="t-text-neutral-20">
        <DividerLine />
      </span>
    </span>
  );
};

const LogWithSerialNumber = ({ log }: { log: AuditLog }) => {
  const { ledger_entry_serial_number, ledger_entry_uuid } =
    log.resource_details || {};
  const title = `${ledger_entry_serial_number} - ${log.resource} ${log.action}`;
  const JELogSlider = useShow();
  return (
    <div className="t-flex t-items-center t-flex-nowrap t-w-full t-gap-1">
      {log.action === AUDIT_LOG_ACTIONS.DELETED ? (
        <>{ledger_entry_serial_number}</>
      ) : (
        <Button customType="link" size="small" onClick={JELogSlider.show}>
          {ledger_entry_serial_number}
        </Button>
      )}
      <span className="t-shrink-0">-</span>
      <span className="t-whitespace-nowrap t-overflow-hidden t-text-ellipsis">
        <TransitionLog log={log} />
      </span>
      <JournalEntryLogSlider
        ledgerEntryId={ledger_entry_uuid!}
        ledgerEntrySerialNumber={ledger_entry_serial_number!}
        title={title}
        isOpen={JELogSlider.shown}
        onClose={JELogSlider.hide}
      />
    </div>
  );
};

const Log = ({
  log,
  showBorder = true,
}: {
  log: AuditLog;
  showBorder: boolean;
}) => {
  return (
    <div
      className={classNames("t-h-max t-pl-2.5 t-relative", {
        "t-border t-border-solid t-border-neutral-10 t-border-t-0 t-border-r-0 t-border-b-0":
          showBorder,
      })}
    >
      <div className="t-absolute t-flex t-left-0 t-items-center t-justify-center -t-translate-x-1/2 t-bg-surface t-h-5 t-w-5 t-text-neutral-20">
        <span className="t-inline-flex t-w-2 t-h-2 t-rounded-full t-bg-neutral-10" />
      </div>
      <div className="t-ml-3 t-space-y-1">
        <div
          className="t-text-subtext-sm t-text-text-60 t-mb-0"
          role="listitem"
        >
          {log.resource_details ? (
            <LogWithSerialNumber log={log} />
          ) : (
            <TransitionLog log={log} />
          )}
        </div>
        <div
          className={classNames(
            "t-text-body-sm t-text-text-30 t-inline-flex t-items-center gap-1",
            {
              "!t-mb-4": showBorder,
            }
          )}
        >
          {log.owner && <Owner owner={log.owner} />}
          {formatDate(log.event.time)} at {formatTime(log.event.time)}
        </div>
      </div>
    </div>
  );
};

export const AuditlogList = ({
  entityId,
  objectId,
  contentClass = "",
}: {
  entityId: string;
  objectId: string;
  contentClass?: string;
}) => {
  const {
    data: invoiceAuditLogs,
    isSuccess,
    isLoading,
    fetchNextPage: loadNext,
    hasNextPage,
  } = useGetInfiniteByObjectIdAuditLogsInfiniteQuery(
    {
      entityId: entityId,
      objectId: objectId,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const allLogs = useMemo(() => {
    return (
      invoiceAuditLogs?.pages?.reduce((acc, page) => {
        return [...acc, ...(page.logs || [])];
      }, [] as AuditLog[]) || []
    );
  }, [invoiceAuditLogs?.pages]);

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isEmpty={allLogs.length === 0}
    >
      <Async.Empty>
        <div className="t-flex t-justify-center t-items-center t-w-full t-h-full t-text-body-sm t-gap-6 t-p-2 t-flex-col">
          <EmptyList />
          <p className="t-flex t-gap-0.5 t-flex-col t-text-center t-text-text-60">
            <span className="t-text-subtext-sm">No logs to show yet</span>
            <span>
              Logs will start showing up here once any activity takes place.
            </span>
          </p>
        </div>
      </Async.Empty>
      <Async.Success>
        <div
          id="scrollableDiv"
          className={classNames("t-h-full t-overflow-auto t-p-5 t-pt-0", {
            [contentClass]: Boolean(contentClass),
          })}
          role="list"
        >
          <InfiniteScroll
            dataLength={allLogs.length}
            next={loadNext}
            hasMore={hasNextPage}
            scrollableTarget="scrollableDiv"
            loader={
              <div className="t-flex t-justify-center t-items-center t-w-full t-text-body-sm t-gap-2 t-p-2">
                <span className="t-flex t-origin-center t-animate-spin">
                  <LoadingIcon />
                </span>
                Loading...
              </div>
            }
            className="t-flex t-flex-col t-gap-3 t-pl-2"
          >
            {allLogs?.map((log, index) => (
              <Log
                key={index}
                log={log}
                showBorder={allLogs.length !== index + 1}
              />
            ))}
          </InfiniteScroll>
        </div>
      </Async.Success>
    </Async.Root>
  );
};
